import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { datetimeFormat } from 'app/constants/date-time-format';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { DateService, FormatService, HelperService, HTTPService, DataService } from 'app/services';
import { followUpSheduleStatus } from 'app/constants/marketing.constants';

@Component({
  selector: 'app-sidepanel-follow-up-campaign',
  templateUrl: './sidepanel-follow-up-campaign.component.html',
  styleUrls: ['./sidepanel-follow-up-campaign.component.scss'],
})
export class SidepanelFollowUpCampaignComponent implements OnInit {
  @Input() followUp: any = {};
  @Input() customer: any = {};
  @Input() currentTime = new Date();
  @Input() isScheduled: boolean = false;
  @Input() isExpanded: boolean = false;
  @Input() isPastFollowup: boolean = false;
  @Input() isMarketingOptOutEnabled: boolean = false;
  @Input() isAutomatedFollowUpEnabled: boolean = false;
  @Input() id: string = '';

  @Input('serverTime')
  set serverTime(serverTime: any) {
    if (serverTime) {
      this.currentTime = serverTime;
    }
  }

  @Output() appFetchFollowUps = new EventEmitter();
  @Output() openContactUpdateDialog = new EventEmitter();

  isScheduling: boolean = false;
  isResetLoading: boolean = false;
  isScheduledCompleted: boolean = false;
  hasCampaingStatusStopped: boolean = false;
  scheduleAt = {
    date: new Date(),
    time: new Date(),
  };
  minDate = new Date();
  contactTooltip: string = '';
  customerPreferredLanguage: string = 'en';

  constructor(
    private dateService: DateService,
    private dataService: DataService,
    private dialogService: DialogService,
    public helperService: HelperService,
    private httpService: HTTPService,
    private formatService: FormatService
  ) { }

  ngOnInit() {
    this.scheduleAt = {
      date: this.followUp.scheduleAt,
      time: this.followUp.scheduleAt,
    };

    if (!this.followUp.isSend && !this.isScheduledCompleted && this.isScheduled && this.followUp.campaignStatus === followUpSheduleStatus.stop) {
      this.hasCampaingStatusStopped = true;
    }

    if (this.followUp.stoppedActivity) {
      this.followUp.stoppedActivity.createdAt = this.dateService.formatDate(
        this.dateService.getCurrentDateTimeTz(new Date(this.followUp.stoppedActivity.createdAt).getTime()),
        datetimeFormat.mmmdyyhhmmaa
      );
      this.followUp.stopTooltip = `${this.followUp.stoppedActivity.actionBy} stopped this follow up at ${this.followUp.stoppedActivity.createdAt}`;
    }

    this.contactTooltip = `
      <div class="tooltip-seondary">
        <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 pb-1">
        ${this.followUp.type === 'EMAIL' ? 'Email' : 'SMS'}
        </div>

        ${(this.followUp.contacts || []).map(
      (contact: string) =>
        `<div class="tooltip-secondary-row font-12 font-secondary-900 py-0 d-flex flex-nowrap">
          <i class="fal fa-${this.followUp.type === 'EMAIL' ? 'envelope' : 'mobile'} font-gray-65 mt-1"></i>
          <span>${contact}</span>
        </div>`
    )}

        ${this.followUp.unsubscribeCount
        ? `<div class="tooltip-secondary-row font-12 font-orange py-0 mt-1">
          <span>${this.followUp.unsubscribeCount} ${this.followUp.type === 'EMAIL' ? 'email' : 'phone'} opted out</span>
        </div>`
        : ''
      }
      </div>
    `;
    if (this.customer && this.customer.preferredLanguage) {
      this.customerPreferredLanguage = this.customer.preferredLanguage;
    }
  }

  handleOpenEstimate(estimateId: any) {
    this.helperService.openPageInNewExistingTab(`/estimate/${estimateId}`, estimateId);
  }

  handleConfirmationDialog(followUp: any) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Show Follow-up with missing date?`,
        message: `${followUp.campaignTitle} contains the "[send_estimate_date]" tag. If estimate is not sent to customer before follow-up date then date field will be empty.`,
        button: {
          success: { title: 'Schedule' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.handleScheduleCampaign(followUp, true);
      }
    });
  }

  handleScheduleCampaign(followUp: any, isForcedSchedule: boolean = false) {
    if (this.dataService.marketingSettings.accessType === 'trial' && this.dataService.marketingSettings.usedFollowUps >= this.dataService.marketingSettings.followUpLimit) {
      this.openTrialCampaignDailog();
    } else {
      if (!isForcedSchedule && followUp.marketingTemplate && followUp.marketingTemplate.text && followUp.marketingTemplate.text.includes('[estimate_send_date]')) {
        this.handleConfirmationDialog(followUp);
        return;
      }

      this.isScheduling = true;
      const followUpCampaign = this.formatService.makeScheduleFollowUpData(followUp, this.customer);
      followUpCampaign.marketingTemplate.text = followUpCampaign.marketingTemplate.text;
      this.httpService
        .scheduleFollowUp(followUpCampaign)
        .then(
          (campaign) => {
            this.isScheduledCompleted = true;
            this.appFetchFollowUps.emit();
            this.openTrialCampaignDailog();
            this.helperService.showToaster('success', 'Follow-up scheduled');
          },
          (err) => {
            this.helperService.showToaster('error', err);
          }
        )
        .finally(() => {
          this.isScheduling = false;
        });
    }
  }

  openTrialCampaignDailog() {
    this.dataService.sendData(this.dataService.typeDialog('trial-campaign-dialog', 'header', null, null, { campaignType: 'followup' }));
  }

  handleDeleteFollowUp(followUp: any, event: Event) {
    event.stopPropagation();

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: 'Update follow-up status',
        message: `Are you sure you want to update the status of follow-up "${followUp.campaignTitle}" scheduled at ${this.dateService.formatDate(followUp.scheduleAt, datetimeFormat.mmmdyyhhmmaa)}`,
        button: {
          success: { title: 'Update' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.isScheduling = true;
        // this.httpService
        //   .deleteScheduleFollowUp(followUp.followUpId)
        //   .then(
        //     (res) => {
        //       this.isScheduledCompleted = true;
        //       this.appFetchFollowUps.emit();
        //       this.helperService.showToaster('success', 'Successfully deleted');
        //     },
        //     (err) => {
        //       this.helperService.showToaster('error', err);
        //     }
        //   )
        //   .finally(() => {
        //     this.isScheduling = false;
        //   });
        this.httpService.updateScheduledFollowup(followUp.followUpId, data).then(
          (res) => {
            this.appFetchFollowUps.emit();
            this.helperService.showToaster('success', 'Follow-up status updated');
          },
          (err) => {
            this.helperService.showToaster('error', err);
          }
        );
      }

      onClose.unsubscribe();
    });
  }

  handleUpdateScheduleAt(followUpId: string, close: any) {
    const date = this.dateService.formatDate(this.scheduleAt.date, datetimeFormat.yyymmdd);
    const time = this.dateService.formatDate(this.scheduleAt.time, datetimeFormat.hhmm);
    const data = { scheduleAt: `${date} ${time}` };

    let scheduleAt: Date;

    if (this.helperService.isAppleDevice) {
      scheduleAt = new Date(this.dateService.madeDateStringIOSCompatible(data.scheduleAt));
    } else {
      scheduleAt = new Date(data.scheduleAt);
    }

    if (this.currentTime.getTime() > scheduleAt.getTime()) {
      this.helperService.showToaster('error', 'Follow-up can only be scheduled for future');
      return;
    }

    if (!followUpId) {
      this.followUp.scheduleAt = scheduleAt;
    }
    if (this.isAutomatedFollowUpEnabled) {
      this.httpService.updateScheduledFollowup(followUpId, data).then(
        (res) => {
          this.followUp.scheduleAt = scheduleAt;
          this.followUp = { ...this.followUp };
          this.helperService.showToaster('success', 'Follow-up schedule updated');
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
    } else {
      this.httpService.updateFollowUpSchedule(followUpId, data).then(
        (res) => {
          this.followUp.scheduleAt = scheduleAt;
          this.followUp = { ...this.followUp };
          this.helperService.showToaster('success', 'Follow-up schedule updated');
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
    }

    close && close();
  }

  handleOpenContactDialog() {
    this.openContactUpdateDialog.emit();
  }

  resetPreference(e: Event, close: any) {
    e.stopPropagation();
    e.preventDefault();

    if (!this.customer._id) return;
    this.isResetLoading = true;
    this.httpService
      .resetCustomerSubscription({ customerId: this.customer._id })
      .then(
        () => {
          if (close) {
            close();
          }
          this.appFetchFollowUps.emit();
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isResetLoading = false;
      });
  }

  handleUpdateStatus(followUp) {
    const data = { status: followUp.campaignStatus === followUpSheduleStatus.scheduled ? followUpSheduleStatus.stop : followUpSheduleStatus.scheduled };
    this.httpService.updateScheduledFollowup(followUp.followUpId, data).then(
      (res) => {
        this.followUp.campaignStatus = data.status;
        this.followUp.statusClass = data.status.toLowerCase();
        this.followUp = { ...this.followUp };
        this.hasCampaingStatusStopped = this.followUp.campaignStatus === followUpSheduleStatus.stop ? true : false;
        if (this.followUp.campaignStatus === followUpSheduleStatus.scheduled) {
          this.followUp.stopTooltip = '';
        } else {
          this.appFetchFollowUps.emit();
        }
        // this.fetchAllFollowups();
        this.helperService.showToaster('success', 'Follow-up schedule updated');
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }
}
